import React, { useEffect, useState } from 'react';
//@ts-ignore
import { graphql } from 'gatsby';
import {
    KontentRichText,
    BrandColor,
    KontentStringValue,
    CTALocation,
    KontentNumberValue,
    KontentRadioMultiChoice,
} from '../../../types/KontentProps';
import { getMultiChoice } from '../Global/DataUtils/index';
import ScrollableBanner from './ScrollableBanner';
import BannerCycle from './BannerCycle';
export type LinkedAlert = {
    id: number;
    elements: {
        message: KontentRichText;
        bar_color: BrandColor;
        font_color: BrandColor;
        activation_date_time: {
            value: Date;
        };
        expiration_date_time: {
            value: Date;
        };
        cta_configuration__cta_label?: KontentStringValue;
        cta_configuration__cta_location?: CTALocation;
        cta_configuration__cta_location_anchor_point?: KontentStringValue;
        cta_configuration__cta_window_action?: {
            value: {
                codename: 'same_window' | 'new_window';
            }[];
        };
        cta_label?: KontentStringValue;
        cta_location?: CTALocation;
        cta_location_anchor_point?: KontentStringValue;
        cta_window_action?: {
            value: {
                codename: 'same_window' | 'new_window';
            }[];
        };
    };
};
interface Props {
    kontentItemId?: string;
    alerts?: {
        value: (LinkedAlert | null)[];
    };
    cycle_between_alerts?: KontentRadioMultiChoice<'yes' | 'no'>;
    time_between_alerts?: KontentNumberValue;
    banner_type?: KontentRadioMultiChoice<'cycle' | 'scroll'>;
    target_link_for_scroll?: CTALocation;
    cta_window_action?: any;
    cta_location_anchor_point?: KontentStringValue;
}

const MultiAlertBanner: React.FC<Props> = ({
    kontentItemId,
    alerts,
    cycle_between_alerts,
    time_between_alerts,
    banner_type,
    target_link_for_scroll,
    cta_window_action,
    cta_location_anchor_point,
}) => {
    const scrollTypeBanner =
        getMultiChoice(banner_type) == 'scroll' ? true : false;
    // Keep the first browser render identical to the prebuilt HTML, even if
    // an alert has expired since the last build.
    const [currentDate, setCurrentDate] = useState<Date | null>(null);

    useEffect(() => {
        setCurrentDate(new Date());
    }, []);

    const activeAlerts = alerts?.value?.filter(
        (alert): alert is LinkedAlert => {
            if (!currentDate || !alert?.elements) return false;
            const activationDate = new Date(
                alert.elements.activation_date_time.value
            );
            const expirationDate = new Date(
                alert.elements.expiration_date_time.value
            );

            return !(
                currentDate.getTime() < activationDate.getTime() ||
                currentDate.getTime() > expirationDate.getTime()
            );
        }
    );
    const alertsToMap =
        activeAlerts?.length === 1
            ? activeAlerts
            : activeAlerts?.length <= 3
            ? [...activeAlerts, ...activeAlerts]
            : activeAlerts;

    return (
        <>
            {alertsToMap?.length ? (
                <div
                    data-multi-alert-banner="true"
                    data-kontent-item-id={kontentItemId}
                >
                    {scrollTypeBanner ? (
                        <ScrollableBanner
                            targetLink={target_link_for_scroll}
                            alerts={alertsToMap}
                            windowAction={cta_window_action}
                            anchor={cta_location_anchor_point}
                        />
                    ) : (
                        <BannerCycle
                            time_between_alerts={time_between_alerts}
                            cycle_between_alerts={cycle_between_alerts}
                            alerts={alertsToMap}
                        />
                    )}
                </div>
            ) : (
                <></>
            )}
        </>
    );
};

export default MultiAlertBanner;

export const fragmentMultiAlertBanner = graphql`
    fragment MultiAlertBanner on kontent_item_component___multi_alert_banner {
        elements {
            cycle_between_alerts {
                value {
                    codename
                }
            }
            time_between_alerts {
                value
            }
            banner_type {
                value {
                    codename
                }
            }
            target_link_for_scroll {
                ...kontentCtaLocation
            }
            cta_window_action {
                value {
                    codename
                }
            }
            cta_location_anchor_point {
                value
            }
            alerts {
                value {
                    ... on kontent_item_content_item___alert_config {
                        id
                        elements {
                            activation_date_time {
                                value
                            }
                            bar_color {
                                ...kontentColor
                            }
                            font_color {
                                ...kontentColor
                            }
                            expiration_date_time {
                                value
                            }
                            message {
                                value
                            }
                            cta_configuration__cta_window_action {
                                value {
                                    codename
                                }
                            }
                            cta_configuration__cta_location_anchor_point {
                                value
                            }
                            cta_configuration__cta_location {
                                ...kontentCtaLocation
                            }
                            cta_configuration__cta_label {
                                value
                            }
                        }
                    }
                }
            }
        }
    }
`;
